import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {Store} from '@ngrx/store';
import {combineLatest, filter, map, Observable, Subject, takeUntil} from 'rxjs';
import {ProposalAppState} from '../../../store/proposal/reducers/proposal.reducer';
import {
  selectDetailPageOffers,
  selectDetailPageProposal, selectDetailPageProposalTile,
  selectLoading,
  selectPreviewUsersAllowedToView,
  selectPrivateNotes,
  selectProposalPreview
} from '../../../store/proposal/selectors/proposal.selectors';
import {MatAccordion, MatExpansionPanel} from '@angular/material/expansion';
import {DialogTwoOptionsComponent} from '../../dialogs/two-options-dialog/dialog-two-options.component';
import {TranslateService} from '@ngx-translate/core';
import {ProposalService} from '../../../services/proposal-service/proposal.service';
import {ProposalStatus} from '../../../enum/proposal-status';
import {Proposal} from '../../../model/proposal/proposal';
import {DialogConfirmComponent} from '../../dialogs/dialog-confirm/dialog-confirm.component';
import {OfferStatus} from '../../../enum/offer-status';
import {Route} from '../../../enum/route';
import {Router} from '@angular/router';
import {UserAppState} from '../../../store/user/reducers/user.reducer';
import {selectUser} from '../../../store/user/selectors/user.selectors';
import {ProposalSelectedActions} from '../../../store/proposal/actions/proposal.actions';
import {User} from '../../../model/user';
import {PrivateNotesDialogComponent} from '../../dialogs/private-notes-dialog/private-notes-dialog.component';
import {PrivateNoteLocation} from '../../../enum/private-note-location';
import {PrivateNote} from '../../../model/offer/private-note';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import {SharePDFDialogComponent} from '../../dialogs/share-proposal-pdf-dialog/share-proposal-pdf-dialog';
import {ProposalTile} from '../../../model/proposal-tile';


(pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs;

import {OfferType} from '../../../enum/offer-type';

@Component({
    selector: 'app-proposal-details-expandable',
    templateUrl: './proposal-details-expandable.component.html',
    styleUrls: ['./proposal-details-expandable.component.scss', '../../../../styles/create-something.scss'],
    standalone: false
})
export class ProposalDetailsExpandableComponent implements OnDestroy, AfterViewInit {

  @Input() isExternalCoverage: boolean = false;
  @Input() isPreview: boolean = false;

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  @ViewChild(MatAccordion) accordion: MatAccordion;

  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;

  disablePartnerInfo$: Observable<boolean>;
  ProposalStatus = ProposalStatus;
  hasOffers: boolean = false;
  hasAcceptedOffers: boolean = false;
  user$ = this.userStore.select(selectUser);
  proposal$ = this.proposalStore.select(selectDetailPageProposal);
  proposalTile$ = this.proposalStore.select(selectDetailPageProposalTile);
  proposalPreview$ = this.proposalStore.select(selectProposalPreview);
  loading$ = this.proposalStore.select(selectLoading);
  usersAllowedToView: User[] = [];

  privateNote$ = this.proposalStore.select(selectPrivateNotes).pipe(
    map(privateNotes => {
      return privateNotes.find(pn => pn.location == PrivateNoteLocation.PROPOSAL_DETAIL);
    })
  );

  @Input() set allDetailsOpen(value: boolean) {
    if (value) {
      this.accordion?.openAll();
    } else {
      this.accordion?.closeAll();
    }
    this._allDetailsOpen = value;
  }

  @Input() createOfferStyle = false;

  _allDetailsOpen = false;
  _isPreview = false;
  get allDetailsOpen() {
    return this._allDetailsOpen;
  }


  @Output() allDetailsOpenChange = new EventEmitter<boolean>();

  constructor(private readonly proposalStore: Store<ProposalAppState>,
              private readonly userStore: Store<UserAppState>,
              readonly proposalService: ProposalService,
              public dialog: MatDialog,
              private readonly router: Router,
              private readonly translate: TranslateService) {
  }


  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }


  ngOnInit() {

    if (!this.isPreview) {
      this.proposal$.subscribe(proposal => {
        this.proposalStore.dispatch(ProposalSelectedActions.loaded({ proposalSelected: proposal}));
        this.proposalStore.select(selectPreviewUsersAllowedToView).subscribe(users => this.usersAllowedToView = users);
        this.proposalStore.dispatch(ProposalSelectedActions.deselect());
      });
      this.proposalStore.select(selectDetailPageOffers).pipe(filter(offers => offers != null),
        takeUntil(this.destroy$)
      ).subscribe(
        offers => {
          this.hasOffers = offers.filter(offer => offer.status != OfferStatus.REJECTION  && offer.offerType !== OfferType.FIXED).length > 0;
          this.hasAcceptedOffers = offers.filter(offer => OfferStatus.ACCEPTED == offer.status && offer.offerType !== OfferType.FIXED).length > 0;
        }
      );
    } else {
      this.proposal$ = this.proposalPreview$;
      this.proposalStore.select(selectPreviewUsersAllowedToView).subscribe(users => this.usersAllowedToView = users);
    }
    this.disablePartnerInfo$ = this.proposalService.checkPartnerInfo(this.proposal$);
  }


  ngAfterViewInit(): void {
    this.panels.changes
      .pipe(
        filter((value: QueryList<MatExpansionPanel>) => value.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe((value: QueryList<MatExpansionPanel>) => {
        const obs: Observable<boolean>[] = [];
        for (const panel of value) {
          obs.push(panel.expandedChange);
        }

        combineLatest(obs).pipe(takeUntil(this.destroy$)).subscribe((array: boolean[]) => {
          if (array.every(value => value)) {
            this.allDetailsOpenChange.emit(true);
          } else if (array.every(value => !value)) {
            this.allDetailsOpenChange.emit(false);
          }
        });
      });

  }

  editProposalClick(proposal: Proposal){
    if (this.hasAcceptedOffers) {
      this.declineEdit(this.translate.instant('editProposal.acceptedOfferExists'));
    }else {
      const proposalId = proposal.proposalInformation.id;
      this.router.navigate(['/' + Route.INSERAT_ERSTELLEN, proposalId])?.then();
    }
  }

  retractOfferClick(proposal: Proposal) {
    if (this.hasAcceptedOffers) {
      this.declineRetract(this.translate.instant('proposalDetails.retractProposal.acceptedOfferExists'));
    } else if (this.hasOffers) {
      this.declineRetract(this.translate.instant('proposalDetails.retractProposal.offerExists'));
    } else {
      this.openRetractMatDialog(proposal);
    }
  }

  openRetractMatDialog(proposal: Proposal) {
    let id = proposal.proposalInformation.id;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    const bodyText = proposal.proposalInformation.proposalName == null ? '' : ` - ${proposal.proposalInformation.proposalName} `;
    dialogConfig.data = {
      title: this.translate.instant('proposalDetails.retractProposal.confirmationDialog.title', {proposalId: id.slice(0, 8)}),
      body: this.translate.instant('proposalDetails.retractProposal.confirmationDialog.body', {
        proposalId: id.slice(0, 8),
        proposalTitle: bodyText
      }),
      optionCancel: this.translate.instant('proposalDetails.retractProposal.confirmationDialog.no'),
      optionConfirm: this.translate.instant('proposalDetails.retractProposal.confirmationDialog.yes'),
    };
    const dialogRef = this.dialog.open(DialogTwoOptionsComponent, dialogConfig);
    dialogRef?.afterClosed().subscribe((result) => {
      if (result) {
        this.proposalService.retract(id).subscribe(() => this.router.navigate(['/' + Route.INSERATE]).then());
      }

    });
  }

  declineEdit(reason) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: reason
    };
    this.dialog.open(DialogConfirmComponent, dialogConfig);
  }

  declineRetract(reason) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: reason
    };
    this.dialog.open(DialogConfirmComponent, dialogConfig);
  }

  protected readonly Date = Date;

  openPrivateNoteDialog() {
    let privateNote: PrivateNote = null;
    let proposalId: string = null;
    this.privateNote$.subscribe(pn => privateNote = pn);
    this.proposal$.subscribe(prop => proposalId = prop.proposalInformation.id);

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      title: this.translate.instant('privateNotesDialog.title'),
      placeholder: this.translate.instant('privateNotesDialog.placeholder'),
      optionSave: this.translate.instant('privateNotesDialog.save'),
      optionCancel: this.translate.instant('privateNotesDialog.cancel'),
      location: PrivateNoteLocation.PROPOSAL_DETAIL,
      note: privateNote?.note,
      id: privateNote?.id,
      proposalId: proposalId
    };
    this.dialog.open(PrivateNotesDialogComponent, dialogConfig);
  }

  openShareProposalPDFDialog(proposal: Proposal, proposalTile: ProposalTile) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      proposal: proposal,
      proposalTile: proposalTile
    };
    this.dialog.open(SharePDFDialogComponent, dialogConfig);
  }

}
